import { AdminRole, hasAnyRole } from '../stores/auth'

/**
 * 权限键表（前端）— R3 角色权限分级框架
 *
 * 本表是 Contract Pack「DOMAIN 权限键」表的前端镜像，必须与后端
 * `backend/src/middleware/permissions.ts` 的 PERMISSIONS 逐条一致（键名 + 角色集）。
 * 任一键的角色集改动，必须前端表、后端表、docs/security 权威表三处同改。
 *
 * 角色缩写：super_admin(SA) admin(A) operator(O) finance(F) finance_manager(FM)
 *           leasing_director(LD，R4 新增)  leasing_manager(LM，R5 新增)
 *           rental_consultant(RC，租房顾问，新增)
 * 全5 = [super_admin, admin, operator, finance, finance_manager]（R3 冻结，不扩容）
 * 全6 = 全5 + leasing_director（R4 新增）
 * 全7 = 全6 + leasing_manager（R5 新增）
 * 全8 = 全7 + rental_consultant（租房顾问新增）
 */
export type PermissionKey =
  | 'DASHBOARD_VIEW'
  | 'LEAD_VIEW'
  | 'LEAD_EDIT'
  | 'LEAD_EDIT_OWN'
  | 'LEAD_TRANSFER'
  | 'LEAD_CLAIM'
  | 'REFERRER_VIEW'
  | 'REFERRER_EDIT_TAGS'
  | 'REVIEW_CENTER'
  | 'REWARD_OPERATE'
  | 'REWARD_PAYOUT'
  | 'REWARD_APPROVE'
  | 'REWARD_ADJUST_AMOUNT'
  | 'PAYMENT_MODIFY'
  | 'BLACKLIST_VIEW'
  | 'ROSTER_MANAGE'
  | 'CONFIG_VIEW'
  | 'CONFIG_EDIT'
  | 'POSTER_UPLOAD'
  | 'UNIT_VIEW'
  | 'UNIT_EDIT'
  | 'DUTY_VIEW'
  | 'DUTY_EDIT'
  | 'USER_VIEW'
  | 'USER_MANAGE'
  | 'REPORT_VIEW'
  | 'AUDIT_VIEW'
  | 'ASSIGNMENT_VIEW'
  | 'ASSIGNMENT_MANAGE'
  | 'CONTENT_EDIT'
  | 'REFERRER_EDIT'
  | 'BUSINESS_CONFIG_EDIT'
  // thankyou_v2（批次 8.2）：商户档案维护 + 资质审核（均 SA/A，32→34）
  | 'MERCHANT_MANAGE'
  | 'QUALIFICATION_REVIEW'
  // P-PROJ-16FA993B0823（2026-10-22）：删除推荐人数据（仅超管，34→35）
  | 'REFERRER_DELETE'

// R3 冻结的 5 角色基线（Plan Pack §2.1「保留原样、不改名、不扩容」），留档如下：
//   ALL5 = ['super_admin', 'admin', 'operator', 'finance', 'finance_manager']
// R4 起全量视图键统一改用 ALL6，ALL5 已无引用点；本项目 tsconfig 开启
// noUnusedLocals，保留未引用的 const 会 TS6133 编译失败，故降级为注释留档。
const ALL6: AdminRole[] = ['super_admin', 'admin', 'operator', 'finance', 'finance_manager', 'leasing_director']
// R5 新增：全7 = 全6 + leasing_manager（运营）。写成 [...ALL6, ...] 展开形式，
// 使 ALL6 保持被引用，规避 noUnusedLocals 的 TS6133（避免重蹈 ALL5 降级为注释）。
const ALL7: AdminRole[] = [...ALL6, 'leasing_manager']
// rental_consultant（租房顾问）新增：全8 = 全7 + rental_consultant。仅追加，ALL7 保留不删。
const ALL8: AdminRole[] = [...ALL7, 'rental_consultant']

/** 权限键 → 允许角色集。逐条等于 Contract Pack DOMAIN 表。 */
export const PERMISSIONS: Record<PermissionKey, AdminRole[]> = {
  DASHBOARD_VIEW: ALL8,
  LEAD_VIEW: ALL8,
  LEAD_EDIT: ['super_admin', 'admin', 'leasing_manager'],
  LEAD_EDIT_OWN: ['operator', 'rental_consultant'],
  LEAD_TRANSFER: ['super_admin', 'admin', 'leasing_director', 'leasing_manager'],
  LEAD_CLAIM: [],
  REFERRER_VIEW: ALL8,
  REFERRER_EDIT_TAGS: ALL7,
  REVIEW_CENTER: ['super_admin', 'admin', 'leasing_manager'],
  REWARD_OPERATE: ['super_admin', 'admin', 'finance', 'finance_manager', 'leasing_manager'],
  REWARD_PAYOUT: ['super_admin', 'admin', 'finance', 'finance_manager'],
  REWARD_APPROVE: ['super_admin', 'admin', 'finance', 'finance_manager'],
  // ST3（2026-10-20，P-PROJ-E3A421DE6203 用户指令，镜像后端）：REWARD_ADJUST_AMOUNT 移出 finance
  // → [SA, A, FM]；PAYMENT_MODIFY 移出 finance、补入 admin → [SA, A, FM]
  REWARD_ADJUST_AMOUNT: ['super_admin', 'admin', 'finance_manager'],
  PAYMENT_MODIFY: ['super_admin', 'admin', 'finance_manager'],
  BLACKLIST_VIEW: ['super_admin', 'admin', 'operator', 'leasing_manager', 'rental_consultant'],
  ROSTER_MANAGE: ['super_admin', 'admin'],
  CONFIG_VIEW: ALL8,
  CONFIG_EDIT: ['super_admin', 'admin'],
  POSTER_UPLOAD: ['super_admin', 'admin'],
  UNIT_VIEW: ALL8,
  UNIT_EDIT: ['super_admin', 'admin'],
  DUTY_VIEW: ALL8,
  DUTY_EDIT: ['super_admin', 'admin', 'leasing_manager', 'leasing_director'],
  // 2026-09-07 收紧（与后端同步）：移出 leasing_manager，仅 SA/A 可管账号
  USER_VIEW: ['super_admin', 'admin'],
  USER_MANAGE: ['super_admin', 'admin'],
  REPORT_VIEW: ['super_admin', 'admin', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager'],
  AUDIT_VIEW: ['super_admin', 'admin', 'leasing_manager'],
  ASSIGNMENT_VIEW: [],
  ASSIGNMENT_MANAGE: ['super_admin', 'admin', 'leasing_director', 'leasing_manager'],
  CONTENT_EDIT: ['super_admin', 'admin'],
  // thankyou_v1（批次 2，镜像后端 middleware/permissions.ts）：零收窄仅 +2 键，30→32
  REFERRER_EDIT: ['super_admin', 'admin'],
  BUSINESS_CONFIG_EDIT: ['super_admin', 'admin'],
  // thankyou_v2（批次 8.2，镜像后端 middleware/permissions.ts）：零收窄仅 +2 键，32→34。
  // MERCHANT_MANAGE 护 /admin/merchants CRUD 与「商户档案」页；QUALIFICATION_REVIEW 护
  // 转介人资质审核（审核人/时间/有效期）。两键均 SA/A，不扩容任何现有角色。
  MERCHANT_MANAGE: ['super_admin', 'admin'],
  QUALIFICATION_REVIEW: ['super_admin', 'admin'],
  // P-PROJ-16FA993B0823（2026-10-22，镜像后端 middleware/permissions.ts）：删除推荐人数据，
  // 仅 super_admin（后端 DELETE /admin/referrers/:id 另有 handler 内 super_admin 二次校验）。
  REFERRER_DELETE: ['super_admin'],
}

/**
 * 集中式权限判定：当前角色是否被授予某权限键。
 * 内部复用 stores/auth.ts::hasAnyRole，保持与既有角色判定语义一致。
 */
export const hasPermission = (
  role: string | null | undefined,
  key: PermissionKey,
): boolean => hasAnyRole(role, ...PERMISSIONS[key])
